import * as Sentry from '@sentry/vue'
import { App } from 'vue'
import { Router } from 'vue-router'

import { appConfig } from '@/config'
import { normalizeSentryUrl } from '@/core/utils/sentry-url'
import type { ClinicType, UserType } from '@/types'

export function initSentry(app: App<Element>, router: Router) {
  if (appConfig.env !== 'development') {
    Sentry.init({
      app,
      release: appConfig.version,
      environment: appConfig.env,
      dsn: appConfig.sentry.dns,
      integrations: [
        Sentry.browserTracingIntegration({
          router,
          beforeStartSpan: context => ({
            ...context,
            name: normalizeSentryUrl(location.pathname),
          }),
        }),
        Sentry.replayIntegration(),
      ],
      tracePropagationTargets: [
        'localhost',
        'node.vue-frontend-ehr.docker',
        'staging.ehr.anzolo.com',
        'ehr.anzolo.com',
      ],
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    })
  }
}

/**
 * Set user context in Sentry for better error tracking and session replay
 */
export function setSentryUser(user: UserType | null, clinic?: ClinicType) {
  if (appConfig.env === 'development') return

  if (!user) {
    Sentry.setUser(null)
    // Clear all user-related contexts and tags to prevent attribution to previous user
    Sentry.setContext('user_details', null)
    Sentry.setContext('clinic', null)
    Sentry.setTag('user_id', undefined)
    Sentry.setTag('clinic_id', undefined)
    Sentry.setTag('role', undefined)
    return
  }

  // Set user identity
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
  })

  // Set additional context
  Sentry.setContext('user_details', {
    is_superuser: user.is_superuser,
    mode: user.mode,
  })

  // Set clinic context if available, otherwise clear it
  if (clinic) {
    Sentry.setContext('clinic', {
      clinic_id: clinic.id,
      clinic_name: clinic.name,
      role: clinic.invite?.role?.name,
      role_id: clinic.invite?.role?.id,
      is_superuser: clinic.invite?.is_superuser,
      permissions: clinic.invite?.permissions
        ? Object.keys(clinic.invite.permissions).filter(key => clinic.invite?.permissions?.[key])
        : [],
    })

    // Set tags for easier filtering in Sentry (convert to string for consistent filtering)
    Sentry.setTag('clinic_id', String(clinic.id))
    if (clinic.invite?.role?.name) {
      Sentry.setTag('role', clinic.invite.role.name)
    } else {
      // Clear role tag when clinic has no invite/role to prevent stale values
      Sentry.setTag('role', undefined)
    }
  } else {
    // Clear clinic context and tags when no clinic is available
    Sentry.setContext('clinic', null)
    Sentry.setTag('clinic_id', undefined)
    Sentry.setTag('role', undefined)
  }

  // Always set user_id tag (convert to string for consistent filtering)
  Sentry.setTag('user_id', String(user.id))
}
